import { ROLES } from '../constants/roles';
import {
  getManageableHouseIds,
  isGlobalPlatformViewer,
  type HouseViewer,
} from './houseAccess';
import { hasCompensationTerms, type CompensationDisplayUser } from './staffCompensation';
import { staffBelongsToHouse } from './staffAssignment';
import { isGhostUser, type UserLike } from './userVisibility';

const BANKER_MANAGED_ROLES = [ROLES.LISTER, ROLES.RUNNER] as const;
const GOD_MANAGED_ROLES = [
  ROLES.BANKER,
  ROLES.LISTER,
  ROLES.RUNNER,
  ROLES.SUPPORT_AGENT,
] as const;

const BANKER_REGISTRABLE_ROLES = [ROLES.LISTER, ROLES.RUNNER] as const;
const GOD_REGISTRABLE_ROLES = [
  ROLES.BANKER,
  ROLES.LISTER,
  ROLES.RUNNER,
  ROLES.SUPPORT_AGENT,
] as const;

export type UserManagementAction =
  | 'register'
  | 'edit'
  | 'suspend'
  | 'delete'
  | 'message'
  | 'payment'
  | 'agreements'
  | 'devices';

function isManagedRole(role: string | undefined, allowed: readonly string[]): boolean {
  return Boolean(role && allowed.includes(role));
}

function sharesViewerHouse(
  viewer: HouseViewer,
  target: HouseViewer,
  activeHouseId?: string,
): boolean {
  if (activeHouseId) return staffBelongsToHouse(target, activeHouseId);
  return getManageableHouseIds(viewer).some((houseId) => staffBelongsToHouse(target, houseId));
}

function viewerHouseScope(viewer: HouseViewer, activeHouseId?: string): string[] {
  if (isGlobalPlatformViewer(viewer)) {
    return activeHouseId ? [activeHouseId] : [];
  }
  return activeHouseId ? [activeHouseId] : getManageableHouseIds(viewer);
}

/** Whether viewer may suspend/lift sanctions on target staff. */
export function canManageUser(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer,
  activeHouseId?: string,
): boolean {
  if (!viewer || isGhostUser(target)) return false;

  if (isGlobalPlatformViewer(viewer)) {
    if (!isManagedRole(target.role, GOD_MANAGED_ROLES)) return false;
    if (activeHouseId) return staffBelongsToHouse(target, activeHouseId);
    return true;
  }

  if (viewer.role === ROLES.BANKER) {
    if (!isManagedRole(target.role, BANKER_MANAGED_ROLES)) return false;
    return sharesViewerHouse(viewer, target, activeHouseId);
  }

  return false;
}

export function canRegisterUserRole(
  viewer: HouseViewer | null | undefined,
  role: string,
  houseId: string,
): boolean {
  if (!viewer || !houseId || role === ROLES.GOD) return false;
  const scope = viewerHouseScope(viewer, houseId);
  if (!scope.includes(houseId) && !isGlobalPlatformViewer(viewer)) return false;

  if (isGlobalPlatformViewer(viewer)) {
    return isManagedRole(role, GOD_REGISTRABLE_ROLES);
  }
  if (viewer.role === ROLES.BANKER) {
    return isManagedRole(role, BANKER_REGISTRABLE_ROLES)
      && getManageableHouseIds(viewer).includes(houseId);
  }
  return false;
}

export function canDeleteUser(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer,
  activeHouseId?: string,
): boolean {
  if (!viewer || isGhostUser(target) || target.role === ROLES.GOD) return false;
  if (isGlobalPlatformViewer(viewer)) {
    if (activeHouseId && !staffBelongsToHouse(target, activeHouseId)) return false;
    return isManagedRole(target.role, GOD_MANAGED_ROLES);
  }
  return false;
}

export function canMessageUser(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer,
  activeHouseId?: string,
): boolean {
  return canManageUser(viewer, target, activeHouseId);
}

export function canEditUserProfile(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer,
  activeHouseId?: string,
): boolean {
  if (!viewer || isGhostUser(target) || target.role === ROLES.GOD) return false;
  if (!isGlobalPlatformViewer(viewer)) return false;
  if (activeHouseId && !staffBelongsToHouse(target, activeHouseId)) return false;
  return isManagedRole(target.role, GOD_MANAGED_ROLES);
}

export function canEditUserPayment(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer & CompensationDisplayUser,
  activeHouseId?: string,
): boolean {
  if (!canManageUser(viewer, target, activeHouseId)) return false;
  return hasCompensationTerms(target);
}

export function canViewStaffDevices(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer,
  activeHouseId?: string,
): boolean {
  if (!canManageUser(viewer, target, activeHouseId)) return false;
  return target.role === ROLES.LISTER || target.role === ROLES.RUNNER;
}

export function canViewUserAgreements(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer & CompensationDisplayUser,
  activeHouseId?: string,
): boolean {
  if (!canViewStaffDevices(viewer, target, activeHouseId)) return false;
  return hasCompensationTerms(target);
}

export function getUserManagementActions(
  viewer: HouseViewer | null | undefined,
  target: UserLike & HouseViewer & CompensationDisplayUser,
  activeHouseId?: string,
): UserManagementAction[] {
  const actions: UserManagementAction[] = [];
  if (canEditUserProfile(viewer, target, activeHouseId)) actions.push('edit');
  if (canManageUser(viewer, target, activeHouseId)) actions.push('suspend');
  if (canMessageUser(viewer, target, activeHouseId)) actions.push('message');
  if (canEditUserPayment(viewer, target, activeHouseId)) actions.push('payment');
  if (canViewUserAgreements(viewer, target, activeHouseId)) actions.push('agreements');
  if (canViewStaffDevices(viewer, target, activeHouseId)) actions.push('devices');
  if (canDeleteUser(viewer, target, activeHouseId)) actions.push('delete');
  return actions;
}

export function filterManageableUsers<T extends UserLike & HouseViewer>(
  viewer: HouseViewer | null | undefined,
  users: T[],
  activeHouseId?: string,
): T[] {
  return users.filter((user) => canManageUser(viewer, user, activeHouseId));
}

export function registrableRolesForViewer(
  viewer: HouseViewer | null | undefined,
): string[] {
  if (isGlobalPlatformViewer(viewer)) return [...GOD_REGISTRABLE_ROLES];
  if (viewer?.role === ROLES.BANKER) return [...BANKER_REGISTRABLE_ROLES];
  return [];
}
